import {Injectable} from '@angular/core';
import {AppointmentDto} from '../data/appointmentDto';
import {ApiService} from '../../app-common/services/server/api.service';
import {Observable, Subject} from 'rxjs';
import {AppointmentType} from '../data/appointmentType.enum';
import {AppointmentReminderTime} from '../data/appointmentRenderTime.enum';
import {QueryCreator, QuerySort} from '../../query/data/queryCreator';
import {Query} from '../../query/data/query';
import {QueryableProperty} from '../../query/data/queryableProperty';
import {ContainsValuePropertyFilter} from '../../query/data/containsValuePropertyFilter';
import {AppointmentProperties} from '../data/appointmentProperties';
import {AppointmentCalendarDto} from '../data/appointmentCalendarDto';
import {EventEx} from '../../app-common/data/Event';
import {LogLevel} from '../../logging/data/logLevel';
import {LogSourceType} from '../../logging/data/logSourceType';
import {LogSource} from '../../logging/data/logSource';
import {AppService} from '../../app-common/services/app/app.service';
import {LoggerService} from '../../logging/services/logger.service';
import {PleaseWaitService} from '../../app-common/services/please-wait/please-wait.service';
import {ConflictException} from '../../app-common/services/server/conflictException';
import {GenericVersion} from '../../audit/data/GenericVersion';

@Injectable({
  providedIn: 'root'
})
export class AppointmentService {
  onConflict: EventEx = new EventEx();
  private refreshAppointmentFlag = new Subject<string>();

  constructor(private apiService: ApiService,
              private appService: AppService,
              private loggerService: LoggerService,
              private pleaseWaitService: PleaseWaitService) {
  }


  listenToRefreshAppointment(): Observable<string> {
    return this.refreshAppointmentFlag.asObservable();
  }

  triggerRefreshAppointment(value?: string): void {
    if (value) {
      this.refreshAppointmentFlag.next('refreshNotifications');
    } else {
      this.refreshAppointmentFlag.next();
    }
  }

  async getAppointments(): Promise<AppointmentDto[]> {
    return await this.apiService.applicationApiClient.get<AppointmentDto[]>(`Appointment/GetAllAppointmentsByUser`);
  }

  async getCalendarApptEntries(): Promise<AppointmentCalendarDto[]> {
    const entries = new Array();
    const dtos = await this.getAppointments();
    for (const dto of dtos) {
      // const calendarEntry = {title: dto.appointment.title, start: dto.appointment.dueDate, apptDto: dto };
      const calendarEntry = new AppointmentCalendarDto(dto.appointment.title, dto.appointment.dueDate, dto);
      entries.push(calendarEntry);
    }
    return entries;
  }

  async getAppointmentHistory(appointmentId: number, force: boolean = false): Promise<GenericVersion[]> {
    if (appointmentId <= 0) {
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.AppointmentService, 'Failed to fetch appointment audits!');
      return undefined;
    }
    return await this.apiService.applicationApiClient.get<GenericVersion[]>(`Appointment/History/${appointmentId}`);
  }

  async getAppointmentVersion(appointmentId: number, version: number, force: boolean = false): Promise<AppointmentDto> {
    if (appointmentId <= 0 || version <= 0) {
      return undefined;
    }
    return await this.apiService.applicationApiClient.get<AppointmentDto>(`Appointment/History/${appointmentId}/${version}`);
  }

  async getAppointmentVersionLabels(appointmentId: number, version: number, force: boolean = false): Promise<string[]> {
    if (appointmentId <= 0 || version <= 0) {
      return undefined;
    }
    try {
      return await this.apiService.applicationApiClient.get<string[]>(`Appointment/History/${appointmentId}/${version}/Labels`);
    } catch (e) {
      return ['INITIAL'];
    }
  }

  async deleteAppointment(appointmentId: number, force: boolean): Promise<any> {
    return await this.apiService.applicationApiClient.delete(`Appointment`, appointmentId).then(() =>
      this.appService.showSuccessMessage('Appointment Deleted', {windowId: 'main'}));
  }

  async editAppointment(appointmentDto: AppointmentDto, force: boolean = false): Promise<AppointmentDto> {
    this.pleaseWaitService.addToQueue();
    let response;
    appointmentDto['force'] = force;
    try {
      response = await this.apiService.applicationApiClient.put<AppointmentDto>(`Appointment`, appointmentDto.appointment.id, appointmentDto);
      this.appService.showSuccessMessage('Appointment Edited', {windowId: 'main'});
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.AppointmentService, {
        entity: appointmentDto,
        message: 'Edit appointment'
      });
      this.pleaseWaitService.removeFromQueue();
      return response;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      if (ex instanceof ConflictException) {
        this.onConflict.emit({my: appointmentDto, db: ex.conflict});
      }
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.AppointmentService, {
        entity: ex,
        message: 'Edit appointment encountered an exception'
      });
      throw ex;
    }

  }

  async revertAppointment(appointment: AppointmentDto, version: number): Promise<AppointmentDto> {
    this.pleaseWaitService.addToQueue();
    let response;
    const appointmentId = appointment.appointment.id;
    try {
      response = await this.apiService.applicationApiClient.get(`Appointment/History/${appointmentId}/${version}/Revert`);
      this.appService.showSuccessMessage('Appointment Edited', {windowId: 'main'});
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.AppointmentService, {
        entity: null,
        message: 'Revert appointment'
      });
      this.pleaseWaitService.removeFromQueue();
      return response;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      if (ex instanceof ConflictException) {
        this.onConflict.emit({my: appointment, db: ex.conflict});
      }
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.AppointmentService, {
        entity: ex,
        message: 'Edit appointment encountered an exception'
      });
    }
  }

  async snooze(appointmentId: number): Promise<any> {
    return await this.apiService.applicationApiClient.patchWithCustomURL(`Appointment/${appointmentId}/Snooze`, appointmentId);
  }

  async getAppointmentById(appointmentId: number): Promise<AppointmentDto> {
    return await this.apiService.applicationApiClient.get<AppointmentDto>(`Appointment/${appointmentId}`);
  }

  async createAppointment(appointmentDto: AppointmentDto): Promise<AppointmentDto> {
    const res = await this.apiService.applicationApiClient.post<AppointmentDto>(`Appointment`, appointmentDto);
    if (res) {
      this.appService.showSuccessMessage('Appointment Created', {windowId: 'main'});
      return res;
    }
  }

  async searchAppointments(searchQuery: string): Promise<AppointmentDto[]> {
    const query = this.buildSearchQueryModel(searchQuery, 9999, 0);
    return await this.apiService.applicationApiClient.getWithParams<AppointmentDto[]>('Appointment/Search', {filter: JSON.stringify(query)});
  }

  async dismissAppointment(appointmentId: number) {
    return await this.apiService.applicationApiClient.patchWithCustomURL(`Appointment/${appointmentId}/Dismiss`, appointmentId);
  }

  async getAllAppointmentsWorthToRemind(): Promise<AppointmentDto[]> {
    return await this.apiService.applicationApiClient.get('Appointment/GetAllAppointmentsWorthToRemind');
  }

  getEmptyAppointmentDto(): AppointmentDto {
    return {
      appointment: {
        appointmentType: AppointmentType.Reminder,
        reminderTime: AppointmentReminderTime.Minutes_30,
        title: '',
        notes: '',
        dueDate: new Date(),
        dismissed: false,
        id: 1,
        modifiedBy: '',
        modifiedDate: new Date(),
        deleted: false,
        createdBy: '',
        version: 1,
        creationDate: new Date()
      },
      appointmentContacts: [],
      additionalUsers: [],
      additionalCustomers: []
    };
  }

  buildSearchQueryModel(searchQuery: string, entriesPerPage: number, pageNumber: number) {
    const customerProperties = new AppointmentProperties();
    const rootQuery = new Query();

    for (const property of customerProperties.properties) {
      if (property.name === 'title') {
        rootQuery.filters.push(this.createContainsFilter(property, searchQuery));
      }
    }
    const sorting = new QuerySort();
    sorting.dir = customerProperties.sortingDescending ? 'desc' : 'asc';
    sorting.field = 'creationDate';

    return QueryCreator.generateFilter(rootQuery, sorting, entriesPerPage, pageNumber, 'and');
  }

  private createContainsFilter(property: QueryableProperty, toSearch: any) {
    const result = new ContainsValuePropertyFilter();
    result.active = true;
    result.property = property;
    result.queryValue = toSearch;
    return result;
  }

}
